import docEditorServiceApi from "@/Services/doc-editor-service-api";
import type { MusicEditorCustomData } from "@/types/MusicEditorCustomData";
import type { GenerateMusicResult } from "./generateMusic";
import { CategoryName } from "./constant";
import { getUserGuidForTracking, getVisitGuidForTracking } from "@/utils/visitor";
import { readCookie } from "./helper";
import { resolveMusicStorageRef } from "./musicExport";
import {
  applyStemSelectionToCustomData,
  mergeMusicEditorCustomData,
  serializeMusicEditorCustomData,
} from "./musicCustomData";

export interface MusicQueryExportOptions {
  convertedFormat?: string;
  stem?: string;
  stemsZipName?: string;
  customData?: Partial<MusicEditorCustomData>;
  /** Post-auth landing page for gift onboarding (track-song). */
  redirectUrl?: string;
  /** Gift-funnel doc profile id — keeps payment/querydata on the same order doc. */
  docId?: string;
}

const musicGenerationCustomDataByJobId = new Map<string, MusicEditorCustomData>();

/** Cached at generation time; appended to queryData on the existing editor-info save. */
export function registerMusicGenerationCustomData(
  musicId: string,
  customData: Partial<MusicEditorCustomData>,
): void {
  const merged = mergeMusicEditorCustomData(
    musicGenerationCustomDataByJobId.get(musicId),
    customData,
  );
  musicGenerationCustomDataByJobId.set(musicId, merged);
}

export function getMusicGenerationCustomData(
  musicId: string,
): MusicEditorCustomData | undefined {
  return musicGenerationCustomDataByJobId.get(musicId);
}

export function clearMusicGenerationCustomData(musicId?: string): void {
  if (musicId) {
    musicGenerationCustomDataByJobId.delete(musicId);
    return;
  }
  musicGenerationCustomDataByJobId.clear();
}

function resolveCustomDataForQuery(
  music: Pick<GenerateMusicResult, "musicId">,
  options?: MusicQueryExportOptions,
): MusicEditorCustomData | undefined {
  const fromCache =
    music.musicId != null
      ? musicGenerationCustomDataByJobId.get(music.musicId)
      : undefined;

  const merged = mergeMusicEditorCustomData(fromCache, options?.customData);
  const withStem = applyStemSelectionToCustomData(merged, options?.stem);
  return serializeMusicEditorCustomData(withStem) ? withStem : undefined;
}

function buildEditorInfoEntity(queryEntry: Record<string, unknown>) {
  const visitId = getVisitGuidForTracking();
  const visitorId =
    readCookie("visitorGuid") ??
    readCookie("visitorid") ??
    readCookie("visitorId") ??
    "";
  const category = CategoryName.MUSIC_GENERATOR_AI;

  return {
    userGuid: getUserGuidForTracking(),
    queryData: JSON.stringify([queryEntry]),
    visitId,
    visitorId,
    category,
  };
}

export function buildMusicQueryDataEntry(
  music: Pick<
    GenerateMusicResult,
    "musicId" | "filename" | "blobPath" | "blobContainer" | "metadataFileName"
  >,
  options?: MusicQueryExportOptions,
): Record<string, unknown> {
  const { folder, fileName } = resolveMusicStorageRef(music);
  const relativeUrl = `${folder}/${fileName}`;
  const origFormat = fileName.split(".").pop()?.toLowerCase() ?? "mp3";
  const category = CategoryName.MUSIC_GENERATOR_AI;
  const customData = serializeMusicEditorCustomData(
    resolveCustomDataForQuery(music, options),
  );

  return {
    url: relativeUrl,
    ...(music.filename && { filename: music.filename }),
    category,
    ...(music.metadataFileName && {
      jsonUrl: music.metadataFileName,
    }),
    ...(options?.stemsZipName && {
      zip: options.stemsZipName,
    }),
    ...(options?.convertedFormat && {
      OriginalFileFormat: origFormat,
      ConvertedFileFormat: options.convertedFormat,
      stem: options.stem,
    }),
    ...(customData && { customData }),
    ...(options?.redirectUrl && { redirectUrl: options.redirectUrl }),
    ...(options?.docId && { docId: options.docId }),
  };
}

export async function saveMusicEditorQueryInfo(
  music: Pick<
    GenerateMusicResult,
    "musicId" | "filename" | "blobPath" | "blobContainer"
  >,
  options?: MusicQueryExportOptions,
): Promise<string | null> {
  const queryEntry = buildMusicQueryDataEntry(music, options);

  return (await docEditorServiceApi.SaveEditorQueryInfo(
    buildEditorInfoEntity(queryEntry),
  )) as string | null;
}
